import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Container, Typography, Chip, CircularProgress,
    Stack, Paper, Tab, Tabs, TextField, MenuItem,
    IconButton, Tooltip, Alert, Collapse, Button, Grid,
} from '@mui/material';
import ArrowBackIcon      from '@mui/icons-material/ArrowBack';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import TwoWheelerIcon     from '@mui/icons-material/TwoWheeler';
import SaveIcon           from '@mui/icons-material/Save';
import LocationOnIcon     from '@mui/icons-material/LocationOn';
import FilterListIcon     from '@mui/icons-material/FilterList';
import CloseIcon          from '@mui/icons-material/Close';
import { useNavigate }    from 'react-router-dom';
import axios              from '../api/axiosConfig';
import { useAuth }        from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';

const TERRACOTTA = '#C4622D';
const WALNUT     = '#2C1F14';
const FOREST     = '#4A6741';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';

const TOUR_STATUS_COLORS = {
    SCHEDULED:   { bgcolor: '#e3f2fd', color: '#1565c0' },
    IN_PROGRESS: { bgcolor: '#fff3e0', color: '#e65100' },
    COMPLETED:   { bgcolor: '#e8f5e9', color: '#2e7d32' },
    CANCELED:    { bgcolor: '#fce4ec', color: '#c62828' },
};

const RENTAL_STATUS_COLORS = {
    PENDING:   { bgcolor: '#fff3e0', color: '#e65100' },
    CONFIRMED: { bgcolor: '#e3f2fd', color: '#1565c0' },
    ACTIVE:    { bgcolor: '#e8f5e9', color: '#2e7d32' },
    COMPLETED: { bgcolor: '#f3e5f5', color: '#6a1b9a' },
    CANCELLED: { bgcolor: '#fce4ec', color: '#c62828' },
};

const TOUR_STATUSES   = ['SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'CANCELED'];
const RENTAL_STATUSES = ['PENDING', 'CONFIRMED', 'ACTIVE', 'COMPLETED', 'CANCELLED'];
const TOUR_TYPES      = ['WALK', 'BIKE', 'BUS'];

// ── Inline earnings setter ────────────────────────────────────────────────────
function EarningsCell({ tour, onSaved }) {
    const [pct,    setPct]    = useState(tour.earningsPercent?.toString() || '');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!pct) return;
        setSaving(true);
        try {
            const res = await axios.put(`/api/tours/earnings/${tour.id}?percent=${pct}`);
            onSaved(res.data);
        } catch { /* silent */ }
        finally { setSaving(false); }
    };

    return (
        <Stack direction="row" spacing={0.5} alignItems="center">
            <TextField size="small" value={pct} onChange={e => setPct(e.target.value)}
                placeholder="%" type="number" inputProps={{ min: 0, max: 100, step: 0.5 }}
                sx={{ width: 68 }} />
            <Tooltip title="Save earnings %">
                <IconButton size="small" onClick={handleSave} disabled={saving || !pct}
                    sx={{ color: FOREST }}>
                    {saving ? <CircularProgress size={14} /> : <SaveIcon fontSize="small" />}
                </IconButton>
            </Tooltip>
            {tour.guideEarnings != null && (
                <Typography variant="caption" fontWeight={700} sx={{ color: FOREST, whiteSpace: 'nowrap' }}>
                    {formatCurrency(tour.guideEarnings, tour.billCurrency || 'EUR')}
                </Typography>
            )}
        </Stack>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function DistrictOpsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const isAdmin   = user?.role === 'ADMIN';
    const isManager = user?.role === 'MANAGER';
    const canAccess = isAdmin || isManager;

    // Raw data
    const [districts,    setDistricts]    = useState([]);
    const [districtId,   setDistrictId]   = useState(null);   // manager's district
    const [districtName, setDistrictName] = useState('');
    const [tours,        setTours]        = useState([]);
    const [rentals,      setRentals]      = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [error,        setError]        = useState('');
    const [tab,          setTab]          = useState(0);

    // Filters
    const [filterDistrict,     setFilterDistrict]     = useState('ALL');
    const [filterAfter,        setFilterAfter]        = useState('');
    const [filterBefore,       setFilterBefore]       = useState('');
    const [filterTourType,     setFilterTourType]     = useState('ALL');
    const [filterTourStatus,   setFilterTourStatus]   = useState('ALL');
    const [filterRentalStatus, setFilterRentalStatus] = useState('ALL');
    const [showFilters,        setShowFilters]        = useState(false);

    const load = useCallback(async () => {
        if (!user?.id) return;
        setLoading(true);
        setError('');
        try {
            if (isAdmin) {
                const [distRes, toursRes, rentalsRes] = await Promise.all([
                    axios.get('/districts/view?size=1000'),
                    axios.get('/api/tours/view?size=1000'),
                    axios.get('/rentals/view?size=1000'),
                ]);
                setDistricts(distRes.data?.content   || distRes.data   || []);
                setTours(toursRes.data?.content      || toursRes.data   || []);
                setRentals(rentalsRes.data?.content  || rentalsRes.data || []);
            } else {
                // Manager: look up their district first
                const mgRes = await axios.get(`/managers/view/id/${user.id}`);
                const dId   = mgRes.data?.districtId;
                const dName = mgRes.data?.districtName || '';
                setDistrictId(dId);
                setDistrictName(dName);
                if (!dId) { setLoading(false); return; }
                const [toursRes, rentalsRes] = await Promise.all([
                    axios.get(`/api/tours/by-district/${dId}`),
                    axios.get(`/rentals/view/district/${dId}`),
                ]);
                setTours(toursRes.data   || []);
                setRentals(rentalsRes.data || []);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load district data.');
        } finally {
            setLoading(false);
        }
    }, [user?.id, isAdmin]);

    useEffect(() => { if (canAccess) load(); }, [load, canAccess]);

    // ── Filtered data ──────────────────────────────────────────────────────────
    const filteredTours = useMemo(() => {
        let list = [...tours];
        if (isAdmin && filterDistrict !== 'ALL') {
            list = list.filter(t => String(t.districtId) === String(filterDistrict));
        }
        if (filterAfter) {
            const after = new Date(filterAfter);
            list = list.filter(t => t.time && new Date(t.time) >= after);
        }
        if (filterBefore) {
            const before = new Date(filterBefore);
            list = list.filter(t => t.time && new Date(t.time) <= before);
        }
        if (filterTourType   !== 'ALL') list = list.filter(t => t.type   === filterTourType);
        if (filterTourStatus !== 'ALL') list = list.filter(t => t.status === filterTourStatus);
        return list;
    }, [tours, isAdmin, filterDistrict, filterAfter, filterBefore, filterTourType, filterTourStatus]);

    const filteredRentals = useMemo(() => {
        let list = [...rentals];
        if (isAdmin && filterDistrict !== 'ALL') {
            list = list.filter(r => String(r.districtId) === String(filterDistrict));
        }
        if (filterAfter) {
            const after = new Date(filterAfter);
            list = list.filter(r => r.startTime && new Date(r.startTime) >= after);
        }
        if (filterBefore) {
            const before = new Date(filterBefore);
            list = list.filter(r => r.startTime && new Date(r.startTime) <= before);
        }
        if (filterRentalStatus !== 'ALL') list = list.filter(r => r.status === filterRentalStatus);
        return list;
    }, [rentals, isAdmin, filterDistrict, filterAfter, filterBefore, filterRentalStatus]);

    const hasActiveFilters =
        (isAdmin && filterDistrict !== 'ALL') ||
        filterAfter !== '' || filterBefore !== '' ||
        filterTourType !== 'ALL' || filterTourStatus !== 'ALL' || filterRentalStatus !== 'ALL';

    const resetFilters = () => {
        setFilterDistrict('ALL');
        setFilterAfter('');
        setFilterBefore('');
        setFilterTourType('ALL');
        setFilterTourStatus('ALL');
        setFilterRentalStatus('ALL');
    };

    // ── Status handlers ────────────────────────────────────────────────────────
    const handleTourStatusChange = async (tourId, status) => {
        try {
            await axios.put(`/api/tours/status/${tourId}?status=${status}`);
            setTours(prev => prev.map(t => t.id === tourId ? { ...t, status } : t));
        } catch { /* silent */ }
    };

    const handleRentalStatusChange = async (rentalId, status) => {
        try {
            await axios.put(`/rentals/edit/${rentalId}/status?status=${status}`);
            setRentals(prev => prev.map(r => r.id === rentalId ? { ...r, status } : r));
        } catch { /* silent */ }
    };

    const handleTourUpdated = (updated) => {
        setTours(prev => prev.map(t => t.id === updated.id ? updated : t));
    };

    if (!canAccess) return null;

    if (loading) return (
        <Box sx={{ minHeight: '100vh', bgcolor: PARCHMENT, display: 'flex',
            alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: TERRACOTTA }} />
        </Box>
    );

    // Title label
    const displayName = isAdmin
        ? (filterDistrict !== 'ALL'
            ? (districts.find(d => String(d.id) === String(filterDistrict))?.name || 'District')
            : 'All Districts')
        : districtName;

    const completedTours   = filteredTours.filter(t => t.status === 'COMPLETED').length;
    const completedRentals = filteredRentals.filter(r => r.status === 'COMPLETED').length;
    const showContent      = isAdmin || !!districtId;

    // Grid columns differ when admin shows a district column
    const tourCols   = isAdmin ? '140px 1fr 150px 110px 140px 180px' : '1fr 160px 110px 140px 180px';
    const rentalCols = isAdmin ? '140px 1fr 160px 120px 130px'       : '1fr 160px 120px 130px';

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: PARCHMENT }}>
            {/* Banner */}
            <Box sx={{ height: 120, background: `linear-gradient(160deg, ${WALNUT} 0%, #3D5E38 50%, ${FOREST} 100%)`,
                position: 'relative', overflow: 'hidden',
                '&::after': { content: '""', position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: 36, background: PARCHMENT, borderRadius: '36px 36px 0 0' } }} />

            <Container maxWidth="xl" sx={{ mt: -3, pb: 8, position: 'relative', zIndex: 1 }}>

                {/* Back + title */}
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <IconButton onClick={() => navigate(-1)} size="small"
                        sx={{ color: FOREST, border: '1px solid rgba(74,103,65,0.25)', borderRadius: 2 }}>
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <Box>
                        <Typography variant="overline" sx={{ color: FOREST, letterSpacing: '0.2em',
                            fontWeight: 700, lineHeight: 1 }}>
                            District Operations
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <LocationOnIcon sx={{ fontSize: '1rem', color: TERRACOTTA }} />
                            <Typography variant="h6" fontWeight={800} sx={{ color: WALNUT,
                                fontFamily: "'Playfair Display', Georgia, serif" }}>
                                {displayName}
                            </Typography>
                        </Stack>
                    </Box>
                </Stack>

                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                {!showContent && !error && (
                    <Alert severity="warning">You are not assigned to a district yet.</Alert>
                )}

                {showContent && (
                    <>
                        {/* Summary chips */}
                        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap">
                            <Chip icon={<DirectionsBikeIcon />}
                                label={`${filteredTours.length} tours · ${completedTours} completed`}
                                sx={{ bgcolor: 'rgba(196,98,45,0.1)', color: TERRACOTTA, fontWeight: 700 }} />
                            <Chip icon={<TwoWheelerIcon />}
                                label={`${filteredRentals.length} rentals · ${completedRentals} completed`}
                                sx={{ bgcolor: 'rgba(74,103,65,0.1)', color: FOREST, fontWeight: 700 }} />
                        </Stack>

                        {/* Filter bar */}
                        <Paper elevation={0} sx={{ mb: 2, borderRadius: 2,
                            border: '1px solid rgba(74,103,65,0.15)',
                            bgcolor: 'rgba(247,243,238,0.8)', px: 2, py: 1.5 }}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Button size="small"
                                    variant={hasActiveFilters ? 'contained' : 'outlined'}
                                    startIcon={<FilterListIcon />}
                                    onClick={() => setShowFilters(p => !p)}
                                    sx={{ color: hasActiveFilters ? 'white' : TERRACOTTA,
                                        bgcolor: hasActiveFilters ? TERRACOTTA : 'transparent',
                                        borderColor: 'rgba(196,98,45,0.35)',
                                        fontWeight: 600, textTransform: 'none',
                                        '&:hover': { bgcolor: hasActiveFilters ? '#9A4A1F' : 'rgba(196,98,45,0.06)' } }}>
                                    Filters{hasActiveFilters ? ' (active)' : ''}
                                </Button>
                                {hasActiveFilters && (
                                    <Tooltip title="Clear all filters">
                                        <IconButton size="small" onClick={resetFilters}
                                            sx={{ color: 'text.secondary' }}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Box sx={{ flexGrow: 1 }} />
                                <Typography variant="caption" color="text.secondary">
                                    {filteredTours.length} tours · {filteredRentals.length} rentals
                                </Typography>
                            </Stack>

                            <Collapse in={showFilters}>
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" fontWeight={700}
                                        sx={{ textTransform: 'uppercase', letterSpacing: '0.08em',
                                            color: 'text.secondary', display: 'block', mb: 1.5 }}>
                                        Filter Operations
                                    </Typography>
                                    <Grid container spacing={2}>

                                        {/* District — admin only */}
                                        {isAdmin && (
                                            <Grid item xs={12} sm={6} md={3}>
                                                <TextField select fullWidth size="small" label="District"
                                                    value={filterDistrict}
                                                    onChange={e => setFilterDistrict(e.target.value)}>
                                                    <MenuItem value="ALL">All Districts</MenuItem>
                                                    {districts.map(d => (
                                                        <MenuItem key={d.id} value={d.id}>
                                                            {d.name || d.region || d.id}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                        )}

                                        {/* After */}
                                        <Grid item xs={12} sm={6} md={isAdmin ? 2 : 3}>
                                            <TextField fullWidth size="small" type="datetime-local"
                                                label="After" value={filterAfter}
                                                onChange={e => setFilterAfter(e.target.value)}
                                                InputLabelProps={{ shrink: true }} />
                                        </Grid>

                                        {/* Before */}
                                        <Grid item xs={12} sm={6} md={isAdmin ? 2 : 3}>
                                            <TextField fullWidth size="small" type="datetime-local"
                                                label="Before" value={filterBefore}
                                                onChange={e => setFilterBefore(e.target.value)}
                                                InputLabelProps={{ shrink: true }} />
                                        </Grid>

                                        {/* Tour type */}
                                        <Grid item xs={12} sm={6} md={isAdmin ? 2 : 2}>
                                            <TextField select fullWidth size="small" label="Tour Type"
                                                value={filterTourType}
                                                onChange={e => setFilterTourType(e.target.value)}>
                                                <MenuItem value="ALL">All Types</MenuItem>
                                                {TOUR_TYPES.map(t => (
                                                    <MenuItem key={t} value={t}>{t}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        {/* Tour status */}
                                        <Grid item xs={12} sm={6} md={isAdmin ? 1 : 2}>
                                            <TextField select fullWidth size="small" label="Tour Status"
                                                value={filterTourStatus}
                                                onChange={e => setFilterTourStatus(e.target.value)}>
                                                <MenuItem value="ALL">All</MenuItem>
                                                {TOUR_STATUSES.map(s => (
                                                    <MenuItem key={s} value={s}>{s}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        {/* Rental status */}
                                        <Grid item xs={12} sm={6} md={isAdmin ? 2 : 2}>
                                            <TextField select fullWidth size="small" label="Rental Status"
                                                value={filterRentalStatus}
                                                onChange={e => setFilterRentalStatus(e.target.value)}>
                                                <MenuItem value="ALL">All</MenuItem>
                                                {RENTAL_STATUSES.map(s => (
                                                    <MenuItem key={s} value={s}>{s}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                    </Grid>
                                </Box>
                            </Collapse>
                        </Paper>

                        {/* Tabs */}
                        <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden',
                            border: '1px solid rgba(74,103,65,0.1)',
                            boxShadow: '0 2px 16px rgba(44,31,20,0.07)' }}>
                            <Tabs value={tab} onChange={(_, v) => setTab(v)}
                                sx={{ px: 2, borderBottom: '1px solid rgba(74,103,65,0.1)',
                                    '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', minWidth: 100 },
                                    '& .Mui-selected': { color: `${TERRACOTTA} !important` },
                                    '& .MuiTabs-indicator': { bgcolor: TERRACOTTA } }}>
                                <Tab label={`Tours (${filteredTours.length})`} />
                                <Tab label={`Rentals (${filteredRentals.length})`} />
                            </Tabs>

                            {/* ── Tours tab ─────────────────────────────────────── */}
                            {tab === 0 && (
                                <Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: tourCols,
                                        gap: 2, px: 3, py: 1.5,
                                        bgcolor: 'rgba(74,103,65,0.05)',
                                        borderBottom: '1px solid rgba(74,103,65,0.1)' }}>
                                        {[
                                            ...(isAdmin ? ['District'] : []),
                                            'Tour / Date', 'Guide', 'Type', 'Status', 'Earnings (%)',
                                        ].map(h => (
                                            <Typography key={h} variant="caption" fontWeight={700}
                                                sx={{ color: 'text.secondary', textTransform: 'uppercase',
                                                    letterSpacing: '0.06em', fontSize: '0.67rem' }}>
                                                {h}
                                            </Typography>
                                        ))}
                                    </Box>

                                    {filteredTours.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
                                            No tours match the current filters.
                                        </Typography>
                                    ) : (
                                        filteredTours.map((tour, i) => {
                                            const sc = TOUR_STATUS_COLORS[tour.status] || { bgcolor: '#f5f5f5', color: '#666' };
                                            const dLabel = isAdmin
                                                ? (tour.districtName || districts.find(d => String(d.id) === String(tour.districtId))?.name || '—')
                                                : null;
                                            return (
                                                <Box key={tour.id} sx={{
                                                    display: 'grid', gridTemplateColumns: tourCols,
                                                    gap: 2, px: 3, py: 1.8, alignItems: 'center',
                                                    bgcolor: i % 2 === 0 ? 'white' : 'rgba(247,243,238,0.7)',
                                                    borderBottom: '1px solid rgba(0,0,0,0.04)',
                                                    '&:last-child': { borderBottom: 'none' },
                                                }}>
                                                    {isAdmin && (
                                                        <Typography variant="body2" fontWeight={600}
                                                            sx={{ color: FOREST, fontSize: '0.82rem' }}>
                                                            {dLabel}
                                                        </Typography>
                                                    )}
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT }}>
                                                            {tour.offerTitle || 'Tour'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {tour.time
                                                                ? new Date(tour.time).toLocaleString('en-GB',
                                                                    { dateStyle: 'medium', timeStyle: 'short' })
                                                                : '—'}
                                                        </Typography>
                                                        {tour.billTotal && (
                                                            <Typography variant="caption" color="text.disabled" display="block">
                                                                Bill: {formatCurrency(tour.billTotal, tour.billCurrency || 'EUR')}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Typography variant="body2"
                                                        sx={{ color: tour.guideName ? WALNUT : 'text.disabled' }}>
                                                        {tour.guideName || '— unassigned —'}
                                                    </Typography>
                                                    {tour.type ? (
                                                        <Chip label={tour.type} size="small"
                                                            sx={{ bgcolor: 'rgba(201,151,58,0.1)', color: GOLD,
                                                                fontWeight: 700, fontSize: '0.68rem', width: 'fit-content' }} />
                                                    ) : <span />}
                                                    <TextField select size="small" value={tour.status || ''}
                                                        onChange={e => handleTourStatusChange(tour.id, e.target.value)}
                                                        sx={{ '& .MuiSelect-select': { py: 0.6, ...sc } }}>
                                                        {TOUR_STATUSES.map(s => (
                                                            <MenuItem key={s} value={s}>{s}</MenuItem>
                                                        ))}
                                                    </TextField>
                                                    <EarningsCell tour={tour} onSaved={handleTourUpdated} />
                                                </Box>
                                            );
                                        })
                                    )}
                                </Box>
                            )}

                            {/* ── Rentals tab ───────────────────────────────────── */}
                            {tab === 1 && (
                                <Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: rentalCols,
                                        gap: 2, px: 3, py: 1.5,
                                        bgcolor: 'rgba(74,103,65,0.05)',
                                        borderBottom: '1px solid rgba(74,103,65,0.1)' }}>
                                        {[
                                            ...(isAdmin ? ['District'] : []),
                                            'Rental / Date', 'Offer', 'Duration', 'Status',
                                        ].map(h => (
                                            <Typography key={h} variant="caption" fontWeight={700}
                                                sx={{ color: 'text.secondary', textTransform: 'uppercase',
                                                    letterSpacing: '0.06em', fontSize: '0.67rem' }}>
                                                {h}
                                            </Typography>
                                        ))}
                                    </Box>

                                    {filteredRentals.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
                                            No rentals match the current filters.
                                        </Typography>
                                    ) : (
                                        filteredRentals.map((r, i) => {
                                            const sc = RENTAL_STATUS_COLORS[r.status] || { bgcolor: '#f5f5f5', color: '#666' };
                                            const dLabel = isAdmin
                                                ? (districts.find(d => String(d.id) === String(r.districtId))?.name || '—')
                                                : null;
                                            return (
                                                <Box key={r.id} sx={{
                                                    display: 'grid', gridTemplateColumns: rentalCols,
                                                    gap: 2, px: 3, py: 1.8, alignItems: 'center',
                                                    bgcolor: i % 2 === 0 ? 'white' : 'rgba(247,243,238,0.7)',
                                                    borderBottom: '1px solid rgba(0,0,0,0.04)',
                                                    '&:last-child': { borderBottom: 'none' },
                                                }}>
                                                    {isAdmin && (
                                                        <Typography variant="body2" fontWeight={600}
                                                            sx={{ color: FOREST, fontSize: '0.82rem' }}>
                                                            {dLabel}
                                                        </Typography>
                                                    )}
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT }}>
                                                            {r.startTime
                                                                ? new Date(r.startTime).toLocaleString('en-GB',
                                                                    { dateStyle: 'medium', timeStyle: 'short' })
                                                                : '—'}
                                                        </Typography>
                                                        {r.endTime && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                → {new Date(r.endTime).toLocaleString('en-GB',
                                                                    { dateStyle: 'short', timeStyle: 'short' })}
                                                            </Typography>
                                                        )}
                                                        <Typography variant="caption" color="text.disabled" display="block">
                                                            #{r.id?.slice(0, 8)}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" sx={{ color: WALNUT, fontWeight: 600 }}>
                                                        {r.offerName || '—'}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {r.days ? `${r.days} day${r.days !== 1 ? 's' : ''}` : '—'}
                                                    </Typography>
                                                    <TextField select size="small" value={r.status || ''}
                                                        onChange={e => handleRentalStatusChange(r.id, e.target.value)}
                                                        sx={{ '& .MuiSelect-select': { py: 0.6, ...sc } }}>
                                                        {RENTAL_STATUSES.map(s => (
                                                            <MenuItem key={s} value={s}>{s}</MenuItem>
                                                        ))}
                                                    </TextField>
                                                </Box>
                                            );
                                        })
                                    )}
                                </Box>
                            )}
                        </Paper>
                    </>
                )}
            </Container>
        </Box>
    );
}
